import { injectable } from 'inversify';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import type { ILoggerConfigInitiator, ILoggerDispatcher } from '@/app.obresvability/interfaces.ts';
import type { IBaseViewModel, SceneItemAction } from '@/engine/app/interfaces.ts';
import type { SliceType } from '@/engine/app/modules/images/interfaces.ts';
import type { CaseInfo } from '@/engine/app/CaseInfoApi.ts';
import { config } from '@/config/app.config';

@injectable()
export class ObservabilityLoggerService implements ILoggerConfigInitiator, ILoggerDispatcher {
  public init(): void {
    if (!this._isValidClientToken(config.datadog.clientToken)) return;
    this._initRealUserMonitoring();
    this._initLogging();
  }

  public setGlobalContext(caseInfo: CaseInfo): void {
    const { surgeryType } = caseInfo;
    const ongoingSurgeryType = localStorage.getItem('surgeryType');

    if (ongoingSurgeryType === null) {
      localStorage.setItem('surgeryType', surgeryType);
    } else if (ongoingSurgeryType === surgeryType) {
      // pass
    } else {
      localStorage.removeItem('surgeryType');
      datadogRum.stopSession();
      location.reload();
    }

    this._setGlobalContext(caseInfo);
  }

  public setGlobalContextProperty<K, V>(key: K, value: V): void {
    datadogRum.setGlobalContextProperty(key, value);
  }

  public startView(descriptor: string): void {
    datadogRum.startView(descriptor);
  }

  public dispatchSceneItemAction(name: SceneItemAction, viewModel: IBaseViewModel): void {
    datadogRum.addAction(name, {
      kind: viewModel.kind,
      label: viewModel.label,
      isVisible: viewModel.isVisible
    });
  }

  public dispatchImageAction(name: string, slice: SliceType, kind?: string): void {
    datadogRum.addAction(name, {
      slice: slice,
      eventKind: kind
    });
  }

  public dispatchUndefinedValueErrorThrowing(vm: IBaseViewModel): void {
    datadogLogs.logger.error(
      `Model value error for ${vm.label}`,
      { kind: vm.kind, label: vm.label },
      new Error('Value is undefined')
    );
  }

  private _setGlobalContext(caseInfo: CaseInfo): void {
    const { sub, surgeryType, caseId, caseCode } = caseInfo;
    datadogRum.setUser({ id: sub });
    datadogRum.setGlobalContext({ surgeryType, caseId, caseCode });
    datadogLogs.setGlobalContext({ surgeryType, caseId, caseCode });
  }

  private _initRealUserMonitoring(): void {
    datadogRum.init({
      actionNameAttribute: 'data-testid',
      applicationId: config.datadog.applicationId,
      clientToken: config.datadog.clientToken,
      site: 'datadoghq.com',
      service: 'planner',
      env: config.datadog.environment,
      version: config.datadog.version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      defaultPrivacyLevel: 'mask-user-input'
    });
  }

  private _initLogging(): void {
    datadogLogs.init({
      clientToken: config.datadog.clientToken,
      site: 'datadoghq.com',
      service: 'planner',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      forwardConsoleLogs: 'all'
    });
  }

  private _isValidClientToken(token: string): boolean {
    const tokenPattern = /^pub[a-f0-9]{32}$/;
    return tokenPattern.test(token);
  }
}
